import { StrandTimeSeriesData } from "@/pages/caster/models/StrandSpeed";
import { getMomentTz } from "@dexteel/mesf-core";
import { EChartsOption } from "echarts-for-react";
import { useMemo } from "react";

const colors = ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd"];

const moment = getMomentTz();

interface UseChartOptionsProps {
	eafValues: StrandTimeSeriesData[] | null | undefined;
	heatName: string;
}

const strandColors: { [key: string]: string } = {
	KWH_Per_Ton: colors[0], // Blue
	Power_MW: colors[1], // Orange
	Steel_Temp: colors[2], // Green
};

// Define which axis each tag should use
const tagAxisMapping: { [key: string]: number } = {
	KWH_Per_Ton: 0,
	Power_MW: 1,
	Steel_Temp: 2,
};

// Define display names for tags
const tagDisplayNames: { [key: string]: string } = {
	KWH_Per_Ton: "KWH/Ton",
	Power_MW: "Power",
	Steel_Temp: "Probe temp",
};

export const useChartOptions = ({
	eafValues,
	heatName,
}: UseChartOptionsProps): EChartsOption => {
	const chartOptions = useMemo<EChartsOption>(() => {
		const data: StrandTimeSeriesData[] = (eafValues || [])
			.map((temp) => ({
				Datetime: temp.Datetime,
				Value: temp.Value ?? 0,
				TagId: temp.TagId?.toString() || "",
				TagName: temp.TagName || "",
			}))
			.filter((item) => item.Datetime && item.TagId && item.TagName);

		if (data.length === 0) {
			return {
				title: {
					left: "center",
					top: "center",
				},
				series: [],
			};
		}

		const groupedData = data.reduce<{ [key: string]: StrandTimeSeriesData[] }>(
			(acc, item) => {
				if (!acc[item.TagName]) {
					acc[item.TagName] = [];
				}
				acc[item.TagName].push(item);
				return acc;
			},
			{},
		);

		// Calculate min/max for each tag type
		const calculateAxisLimits = (tagName: string) => {
			const tagData = groupedData[tagName] || [];
			if (tagData.length === 0) return { min: 0, max: 100, interval: 20 };

			const values = tagData.map((item) => item.Value);
			const minValue = Math.min(...values);
			const maxValue = Math.max(...values);

			const margin = Math.ceil((maxValue - minValue) * 0.05) || 1;
			const yMin = Math.max(0, Math.floor(minValue) - margin);
			const yMax = Math.ceil(maxValue + margin);
			const interval = Math.ceil((yMax - yMin) / 5) || 1;

			return { min: yMin, max: yMax, interval };
		};

		return {
			animation: false,
			toolbox: {
				feature: {
					saveAsImage: {
						name: `Heat ${heatName} - EAF Process Data`,
						pixelRatio: 2,
					},
				},
			},
			dataZoom: [
				{
					type: "inside",
					show: true,
				},
			],
			tooltip: {
				trigger: "axis",
				formatter: (params: any) => {
					const datetime = moment(params[0].value[0]).format(
						"yyyy-MM-DD HH:mm:ss",
					);

					const tooltipContent = params
						.map((param: any) => {
							let unit = "";
							const seriesName = param.seriesName;
							if (seriesName === "Power") unit = " MW";
							else if (seriesName === "Probe temp") unit = " °F";
							else if (seriesName === "KWH/Ton") unit = " kWh/ton";

							return `${seriesName}: ${param.value[1].toFixed(1)}${unit}`;
						})
						.join("<br>");

					return `Time: ${datetime}<br>${tooltipContent}`;
				},
			},
			legend: {
				data: Object.keys(groupedData)
					.map((tagName) => tagDisplayNames[tagName] || tagName)
					.sort(),
				top: "bottom",
				selectedMode: true,
				textStyle: {
					fontSize: 10,
				},
				itemGap: 20,
			},
			xAxis: {
				type: "time",
				axisLabel: {
					formatter: (value: number) => {
						return moment(value).format("HH:mm");
					},
				},
			},
			yAxis: [
				// Y-axis 0: KWH_Per_Ton
				{
					type: "value",
					name: "kWh/ton",
					nameTextStyle: {
						fontSize: 12,
						color: strandColors.KWH_Per_Ton,
						align: "left",
					},
					nameLocation: "end",
					position: "left",
					axisLabel: {
						color: strandColors.KWH_Per_Ton,
					},
					axisLine: {
						lineStyle: {
							color: strandColors.KWH_Per_Ton,
						},
					},
					...calculateAxisLimits("KWH_Per_Ton"),
				},
				// Y-axis 1: Power_MW
				{
					type: "value",
					name: "MW",
					nameTextStyle: {
						fontSize: 12,
						color: strandColors.Power_MW,
						align: "right",
					},
					nameLocation: "end",
					position: "right",
					axisLabel: {
						color: strandColors.Power_MW,
					},
					axisLine: {
						lineStyle: {
							color: strandColors.Power_MW,
						},
					},
					...calculateAxisLimits("Power_MW"),
				},
				// Y-axis 2: Steel_Temp
				{
					type: "value",
					name: "°F",
					nameTextStyle: {
						fontSize: 12,
						color: strandColors.Steel_Temp,
						align: "left",
					},
					nameLocation: "end",
					position: "left",
					offset: 80,
					axisLabel: {
						color: strandColors.Steel_Temp,
					},
					axisLine: {
						lineStyle: {
							color: strandColors.Steel_Temp,
						},
					},
					...calculateAxisLimits("Steel_Temp"),
				},
			],
			series: Object.entries(groupedData).map(([tagName, tagData]) => {
				const displayName = tagDisplayNames[tagName] || tagName;
				const isProbeTemp = tagName === "Steel_Temp";

				return {
					name: displayName,
					type: "line",
					step: isProbeTemp ? "middle" : undefined,
					smooth: !isProbeTemp,
					symbol: isProbeTemp ? "circle" : "none",
					symbolSize: isProbeTemp ? 4 : undefined,
					sampling: "lttb",
					yAxisIndex: tagAxisMapping[tagName] || 0,
					itemStyle: {
						color: strandColors[tagName] || colors[0],
					},
					lineStyle: {
						width: isProbeTemp ? 2 : 1.5,
					},
					data: tagData.map((item) => [item.Datetime.getTime(), item.Value]),
				};
			}),
		};
	}, [eafValues, heatName]);

	return chartOptions;
};
